import type { RouteLocationRaw } from 'vue-router'

import type { AvailableLocale } from '@/i18n/types'
import { AdvAction } from '@/modules/adv'
import type { VUE_APP_CONFIG } from '@/modules/config/createConfig'
import type { MessageAction } from '@/modules/main-frame/message-types'
import type { SelectedExperimentsData } from '@/server/experiments/types'
import type { PageStatus } from '@/store/app-store'

export enum AdProviderNames {
    GOOGLE_AD = 'googleAd',
    GOOGLE_INSTREAM = 'googleInstream',
    GOOGLE_AD_SENSE = 'googleAdSense',
    GAME_DISTRIBUTION = 'gameDistribution',
    MEMBRANA = 'membrana',
    MEMBRANA_VAST = 'membrana_vast',
    DEBUG_PROVIDER = 'debug',
    EMPTY_PROVIDER = 'emptyProvider',
    DIRECT = 'direct',
    GOOGLE_INSTREAM_CHEAP = 'googleInstream_cheap',
}

export type TMessageAction = MessageAction & AdvAction

export type GameEvent = Omit<MessageEvent, 'data'> & {
    source: MessageEventSource
    data: {
        id: string
        action: TMessageAction
        payload?: Record<string, unknown>
        responseToId?: string
        type: 'adv' | 'liveness' | 'error'
    }
}

export type PaginationType = {
    page: number
    pageSize: number
}

export type PlatformType = 'ios' | 'android' | 'desktop'

export type APIGameInfoDTO = APIGameInfoType | APIRedirect

export type APIGameInfoType = {
    game: APIGameType
    categoriesInfo: APICategoryShortInfo[]
    pathCategories: APICategoryShortInfo[]
}

export type APIRedirect = {
    redirectTarget:
        | {
              type: 'main-page'
              mechanism: string
              reason: string
          }
        | {
              type: 'category'
              cat_type: 'tag' | 'series' | 'category'
              cat_hru: string
              mechanism: string
              reason: string
          }
        | {
              type: 'game'
              game_hru: string
              mechanism: string
              reason: string
          }
    status: 301 | 302
}

export type APIGameType = {
    id: string
    hru: string
    title: string
    icon: string
    preview: string
    description: string
    developer?: string
    how_to_play_text: string
    hasInAppPurchases: boolean
    seo_text: string
    is_hidden: boolean
    is_android: boolean
    is_ios: boolean
    is_desktop: boolean
    tag: string[]
    categories: string[]
    series: string[]
    game_url: string
    screenshots: string[]
    videos: APIGameVideoType[]
    vertical: boolean
    horizontal: boolean
    source?: string
    rating: {
        value: number
        count: number
    }
    createdAt: string
    updatedAt: string
}

export type GameCategoriesInfoType = {
    categoriesInfo: CategoryShortInfo[]
    pathCategories: CategoryShortInfo[]
}

export type ShortGameType = {
    hru: string
    preview: string
    icon?: string
    title: string
    videoId?: string
    videoUrl?: string
    hasInAppPurchases?: boolean
    debugInfo?: unknown
}

export type ShortGameZipType = [
    hru: string,
    title: string,
    preview: string,
    icon: string | undefined,
    videoId: string | undefined,
    videoUrl: string | undefined,
    hasInAppPurchases: boolean,
    rankingDebug: unknown,
]

export type APIShortGameType = {
    hru: string
    icon: string
    id: string
    preview: string
    title: string
    videos: APIGameVideoType[]
    video_metadata: APIGameVideoMetadataType[]
    hasInAppPurchases: boolean
    rankingDebug?: { data: unknown }
}

export type APIGameVideoType = {
    player_url: string
    preview_url: string
    thumbnail_url: string
}

export type APIGameVideoMetadataType = {
    video_type: 'gameplay'
    playgama_id: string
    external_url: string
}

export type MainCategoriesType =
    | 'io'
    | 'action'
    | 'puzzle'
    | 'arcade'
    | 'sports'
    | 'strategy'
    | 'bubble-shooter'
    | 'girls'
    | 'boys'
    | 'car'
    | 'simulation'
    | 'shooting'
    | 'gun'
    | 'clicker'
    | 'funny'
    | 'snake'
    | 'skill'
    | 'drawing'
    | 'idle'
    | 'multiplayer'
    | 'solitaire'
    | 'horror'
    | 'two-player'
    | 'trending_now'
    | 'new'
    | 'random'

export type GameRecommendationType = 'recommended' | 'top_playgama' | 'related' | 'random' | 'series'
export type APIGameRecommendation<T extends GameRecommendationType[]> = {
    [key in T[number]]: APIShortGameType[]
}

export type CategoryType = 'tag' | 'series' | 'category'

export type CategoryDataType = {
    categories: APICategoryData[]
    pagination: {
        page: 0
        pageSize: 0
    }
}

export type APIP2ECoinsInfo = {
    dailyBonus: number
    playGameBonus: number
    playGameAmount: number
    playGameSeconds: number
    randomGameBonus: number
    randomGameSeconds: number
    registerBonus: number
    referralRegisterBonus: number
    referralInviteBonus: number
    percentageToPayOut: number
    signInBonus: number
    inviteBonus: number
}

export type APIGeneralInfo = {
    categories: APICategoryShortInfo[]
    p2e: {
        coinsInfo: APIP2ECoinsInfo
    }
}

export type APICategoryDataType = {
    categories: APICategoryData[]
    pagination: {
        page: 0
        pageSize: 0
    }
}

export type APICategoryData = {
    id: string
    hru: string
    title: string
    categoryType: CategoryType
    games: APIShortGameType[]
    totalCount: number
}

export type APICategoryShortInfo = {
    id: string
    hru: string
    title: string
    categoryType: CategoryType
}

export type CategoryShortInfo = {
    id: string
    title: string
    href: RouteLocationRaw
    categoryType: CategoryType
}

export type APICategorySeoInfo =
    | {
          id: string
          hru: string
          seoText: string
          description: string // full name of categories
          subCategories: { title: string; id: string; hru: string }[]
          pathCategories: { title: string; id: string; hru: string }[]
          metaTitle?: string
          categoryType: CategoryType
          metaDescription?: string
          localizations: {
              lang: AvailableLocale
              hru: string
          }[]
      }
    | APIRedirect

export type CategoryInfo = {
    id: string
    hru: string
    fullTitle: string
    seoText: string
    metaTitle?: string
    metaDescription?: string
    localizations: {
        lang: AvailableLocale
        hru: string
    }[]
    subCategories: { title: string; id: string; href: RouteLocationRaw }[]
    pathCategories: { title: string; id: string; href: RouteLocationRaw }[]
}

export type FormSubmitData = {
    message: string
    email: string
    page?: string
}

export type UserSocialKeys = 'telegramAcc' | 'tonWallet'
export const SOCIALS = ['telegram', 'ton'] as const
export type SocialsName = (typeof SOCIALS)[number]
export type SocialType = 'account' | 'wallet'

export type UserSocialsType = {
    name: SocialsName
    account?: string
    type: SocialType
    tag: string
    key: UserSocialKeys
    isSupport: boolean
    validate: (value: string) => boolean
}

export type User = {
    _id: string
    firstName: string
    lastName: string
    avatar: string
    fennecs: number
    email?: string
    referralCode: string
    telegramAcc?: string
    tonWallet?: string
    gamesPlayed?: number
    createdAt?: number
    currentStreak?: number
    usersInvited?: number
    goldFenecBalance?: number
}

export interface GameAttributes {
    clid?: string
    widgetId?: string
    userAcquisitionParams: Record<string, string>
}

export type TaskType = 'VISIT_EVERYDAY' | 'REFERRAL_INVITE' | 'RANDOM_GAME' | 'PLAY_GAME' | 'REFERRAL_REGISTER'
export type TaskStatus = 'ACTIVE' | 'COMPLETED' | 'CLAIMED'

export type BaseTaskDto = {
    task_id: string
    type: TaskType
    status: TaskStatus
    coins: number
}

export type DailyTaskDto = BaseTaskDto & { type: 'VISIT_EVERYDAY' }
export type RandomTaskDto = BaseTaskDto & {
    type: 'RANDOM_GAME'
    settings: {
        game: ShortGameType
        seconds_to_play: number
    }
}

export type GameTaskDto = BaseTaskDto & {
    type: 'PLAY_GAME'
    settings: {
        game: ShortGameType
        seconds_to_play: number
    }
}

export type ReferralTaskDto = BaseTaskDto & {
    type: 'REFERRAL_INVITE'
    settings: {
        invitations_not_claimed: number
    }
}

export type TaskDto = DailyTaskDto | RandomTaskDto | GameTaskDto | ReferralTaskDto

export type UserTasksStats = {
    wallet: {
        coins: number
    }
    tasks: TaskDto[]
}

export type AdditionalServerData = {
    conversionValues?: {
        prerollEndValue: number
        gameValue: number
        timespent5MinValue: number
    }
    locale: {
        countryCode?: string
        countryNameEn?: string
        currency: Currency
        detectedLocale?: AvailableLocale
    }
    hasToken: boolean
    isGooglebot: boolean
    experiments: SelectedExperimentsData
    config: VUE_APP_CONFIG
}

export type RewardCardTypeDTO = {
    id: string
    type: 'roblox' | 'amazon'
    availability: 'low' | 'medium' | 'high'
    value: number
    price: number
}

export type RewardCardType = {
    id: string
    type: 'roblox' | 'amazon'
    availability: 'low' | 'medium' | 'high'
    value: number
    currency: Currency
    price: number
}

export type Currency = 'eur' | 'usd'

export type RewardPurchasePayload = { rewardId: string; userEmail: string; countryCode: string }

export type LinkTarget = '_blank' | '_self'

export type ImportWrapper = <T>(importCallback: () => T) => () => T

export type ServerRenderContext = {
    pageStatus?: PageStatus
    platform: PlatformType
    auid?: string
    host: string
    referer?: string
    isIframe?: boolean
    detectedLocale?: AvailableLocale
}

export type ExportMode = 'portal' | 'widget' | 'unknown'

export type InstreamData = {
    isVisible: boolean
    secondsLeft: number
    currentAd: number
    totalAds: number
}

export const RedirectReason = {
    NO_LOCALE_IN_ROUTE: 'no_locale_in_route',
    PREFERRED_LOCALE: 'preferred_locale',
    REMOVE_EN_LOCALE: 'remove_en_locale',
    CATEGORY_TYPE_MISMATCH: 'category_type_mismatch',
} as const

export const RedirectMechanism = {
    FRONTEND_ROUTER: 'frontend_router',
    FRONTEND_REDIRECT: 'frontend_redirect',
} as const

export type PageType = 'category' | 'tag' | 'series'
